import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, fieldVisible, fieldWritable, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { categoryLabels, useCategories } from '../catalog/ItemForm';
import { formatPln } from '../catalog/money';
import { isPacklistPick, pickSearch, pickTarget, returnUrl } from '../catalog/pick';
import { usePlaceTitle } from '../nav/NavSteps';
import { useUrlState } from '../nav/urlState';
import { RowMenu } from '../ui/RowMenu';
import { isLocalPath } from '../nav/localPath';
import { CatalogMatchPanel } from './CatalogMatchPanel';
import { PriceForm } from './PriceForm';
import { rentalPickSearch, rentalPickTarget, rentalReturnUrl } from './rentalPick';
import { parseSupplierAdd, parseSupplierEdit } from './supplierAdd';
import { mayAddSupplierPosition } from './SupplierLacksItem';
import { filterPositions, positionName, positionStatsLines, seesSupplierStats, supplierGroups, type PositionStats } from './supplierCatalog';
import { PRICES_RESOURCE, SUBRENTALS_KEY, type PriceView, type SupplierSummary } from './types';
import './subrentals.css';
import { Notice } from '../ui/Notice';

const useSuppliers = () => useQuery({ queryKey: [...SUBRENTALS_KEY, 'suppliers'], queryFn: () => api<{ suppliers: SupplierSummary[] }>('/api/subrentals/suppliers') });

/**
 * The picking mode of the suppliers' catalogs: „Wybierz z katalogu” of the quote (B1) or „Wskaż w katalogu” of
 * „Dopożycz” (owner 2026-10-23 21:23) — where to go back, how to keep the mode between pages, where a pick goes.
 */
type PickMode = { back: string; backLabel: string; search: string; notice: string; returnTo: (p: PriceView, supplierId: string) => string };
function pickModeOf(params: URLSearchParams): PickMode | null {
  const quote = pickTarget(params);
  // owner 2026-10-24 11:15: the packlist's „Wybierz z katalogu”; a position of MY item comes back as my item (#145)
  if (quote && isPacklistPick(quote)) {
    return { back: quote.back, backLabel: 'Wróć do sprzętówki', search: pickSearch(quote), notice: 'Wybierasz pozycję do sprzętówki z katalogu dostawcy: kliknij „Wybierz” przy pozycji — wrócisz do sprzętówki z dodaną pozycją.', returnTo: (p) => returnUrl(quote, p.itemId ? { kind: 'item', id: p.itemId } : { kind: 'dostawca', id: p.id }) };
  }
  if (quote) {
    return { back: quote.back, backLabel: 'Wróć do kosztorysu', search: pickSearch(quote), notice: 'Wybierasz pozycję do kosztorysu z katalogu dostawcy: kliknij „Wybierz” przy pozycji — wrócisz do kosztorysu z dodaną pozycją.', returnTo: (p) => returnUrl(quote, { kind: 'dostawca', id: p.id }) };
  }
  const rental = rentalPickTarget(params);
  if (rental) {
    return { back: rental.back, backLabel: 'Wróć do dopożyczenia', search: rentalPickSearch(rental), notice: 'Wskazujesz pozycję dostawcy do dopożyczenia: kliknij „Wybierz” przy pozycji — wrócisz do formularza „Dopożycz” z wybraną pozycją.', returnTo: (p, supplierId) => rentalReturnUrl(rental, supplierId, p.id) };
  }
  return null;
}

/**
 * The bar over a supplier's pages (owner 2026-10-21 00:09: "strony dostawców w innej kolorystyce, żeby było wiadomo,
 * że to nie moja strona"): the supplier's name on a calm cool band, a way back to the suppliers and to our catalog.
 */
function SupplierBar({ title, pick, back }: { title: string; pick: PickMode | null; back?: boolean }) {
  return (
    <div className="supplier-bar" role="banner" aria-label="Katalog dostawcy">
      <div className="supplier-bar-title">
        {back && (
          <Link className="supplier-bar-link" to={`/katalog/dostawcy${pick?.search ?? ''}`}>
            ← Wszyscy dostawcy
          </Link>
        )}
        <h1>{title}</h1>
      </div>
      <Link className="btn small" to={pick ? pick.back : '/katalog'}>
        {pick ? pick.backLabel : 'Nasz katalog'}
      </Link>
    </div>
  );
}

/** „Dopasuj do mojego katalogu” links positions with our items: the right to change them and their field „Pozycja naszego katalogu”, and to see our catalog. */
export const mayMatchCatalog = (me: Me) => can(me, PRICES_RESOURCE, 'edit') && fieldWritable(me, PRICES_RESOURCE, 'itemId') && can(me, 'catalog.items', 'view');

/** What the picking mode is for (B1 / „Wskaż w katalogu”). */
function PickNotice({ pick }: { pick: PickMode }) {
  return (
    <p className="notice pick-notice" role="status">
      {pick.notice}
    </p>
  );
}

/** `/katalog/dostawcy`: the suppliers to choose from, with how many positions their catalogs have. */
export function SupplierListPage() {
  const { me } = useMe();
  const { params } = useUrlState();
  const pick = pickModeOf(params);
  const suppliers = useSuppliers();
  const [matching, setMatching] = useState(false);
  usePlaceTitle('Katalogi dostawców');
  if (!me) return null;
  const list = suppliers.data?.suppliers ?? [];
  return (
    <section className="page supplier-scope" data-feature-id="subrentals.supplier_catalog">
      <SupplierBar title="Katalogi dostawców" pick={pick} />
      {pick && <PickNotice pick={pick} />}
      <p className="lede">Wybierz dostawcę, żeby zobaczyć jego sprzęt: cenę wynajmu, ilość, opis i uwagi.</p>
      {!pick && mayMatchCatalog(me) && !matching && list.length > 0 && (
        <div className="actions">
          <button className="btn" type="button" onClick={() => setMatching(true)} data-feature-id="subrentals.catalog_match">
            Dopasuj do mojego katalogu
          </button>
        </div>
      )}
      {matching && <CatalogMatchPanel onClose={() => setMatching(false)} />}
      {suppliers.error && <p className="alert" role="alert">{suppliers.error instanceof ApiError ? suppliers.error.message : 'Nie udało się wczytać dostawców'}</p>}
      {suppliers.data && list.length === 0 && <p className="muted">Nie ma jeszcze dostawców. Dodaj kontrahenta z rodzajem „dostawca” w „Kontrahenci”.</p>}
      {list.length > 0 && (
        <table className="table supplier-list" aria-label="Dostawcy">
          <thead>
            <tr>
              <th scope="col">Dostawca</th>
              <th scope="col" className="num">
                Pozycji w katalogu
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td>
                  <Link to={`/katalog/dostawcy/${s.id}${pick?.search ?? ''}`}>{s.name}</Link>
                </td>
                <td className="num">{s.positions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * `/katalog/dostawcy/:id` (owner 2026-10-21 00:08): the supplier's catalog laid out like ours — categories, search —
 * with only the price for rent, how many they have, description and notes. No comparison with our items (00:09).
 */
export function SupplierCatalogPage() {
  const { me } = useMe();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { params, update } = useUrlState();
  const pick = pickModeOf(params);
  const search = params.get('szukaj') ?? '';
  const categoryId = params.get('kategoria') ?? '';
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [matching, setMatching] = useState(false);
  // „Dodaj w katalogu dostawcy” (owner 2026-10-23 20:59): `?dodaj=item:<id>&powrot=…` opens the new-position form with
  // our item; after saving „Wróć” goes back (kept here once the address is cleaned)
  const deepAdd = pick ? null : parseSupplierAdd(params);
  const [added, setAdded] = useState<{ name: string; back: string | null } | null>(null);
  const addItem = useQuery({
    queryKey: ['catalog', 'item', deepAdd?.itemId ?? ''],
    enabled: !!deepAdd && !!me && mayAddSupplierPosition(me),
    retry: false,
    queryFn: () => api<{ id: string; name: string; categoryId?: string | null }>(`/api/catalog/items/${encodeURIComponent(deepAdd!.itemId)}`),
  });
  const suppliers = useSuppliers();
  const canViewCategories = !!me && can(me, 'catalog.categories', 'view');
  const categories = useCategories(canViewCategories).data?.categories ?? [];
  const prices = useQuery({
    queryKey: [...SUBRENTALS_KEY, 'prices', 'supplier', id],
    queryFn: () => api<{ prices: PriceView[] }>(`/api/subrentals/prices?supplierId=${encodeURIComponent(id)}`),
  });
  // the details' statistics (owner 2026-10-22 00:07): one request for the whole catalog, on the first opened details
  // review M2: the numbers cover every event — only with the rentals in scope „wszystkie”
  const seeStats = !!me && seesSupplierStats(me);
  const stats = useQuery({
    queryKey: [...SUBRENTALS_KEY, 'stats', id],
    enabled: seeStats && open !== null,
    queryFn: () => api<{ stats: PositionStats[] }>(`/api/subrentals/suppliers/${encodeURIComponent(id)}/stats`),
  });
  const remove = useMutation({
    mutationFn: (p: PriceView) => api(`/api/subrentals/prices/${p.id}?version=${p.version}`, { method: 'DELETE' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY }),
  });
  // Ctrl+klik on a supplier's line (owner 2026-10-23 20:53): `?edytuj=<priceId>` (or `item:<itemId>`) opens that
  // position's editor — without the right to edit its details, or just the row marked — scrolled to and marked for a
  // moment; a position no longer there says so calmly. `powrot` keeps „Wróć” to the quote / packlist.
  const deepEdit = pick || deepAdd ? null : parseSupplierEdit(params);
  const deepKey = deepEdit ? ('priceId' in deepEdit ? deepEdit.priceId : `item:${deepEdit.itemId}`) : null;
  const backRaw = params.get('powrot');
  const back = !pick && !deepAdd && backRaw && isLocalPath(backRaw) ? backRaw : null;
  const [missing, setMissing] = useState(false);
  // owner 2026-10-24 10:53 („zapis i powrót do kosztorysu”): the editor opened from a line — its save goes back there
  const [fromLine, setFromLine] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const flashed = useRef<string | null>(null);
  useEffect(() => {
    if (!deepEdit || !me || !prices.data) return;
    const p = prices.data.prices.find((x) => ('priceId' in deepEdit ? x.id === deepEdit.priceId : x.itemId === deepEdit.itemId));
    if (p) {
      setMissing(false);
      if (!pick && can(me, PRICES_RESOURCE, 'edit')) {
        setEditing(p.id);
        setFromLine(p.id);
      }
      else if (seesSupplierStats(me)) setOpen(p.id);
      flashed.current = null;
      setFlash(p.id);
      update({ edytuj: null, szukaj: null, kategoria: null });
    } else {
      setMissing(true);
      update({ edytuj: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepKey, prices.data, me]);
  useEffect(() => {
    if (!flash || flashed.current === flash) return;
    const el = document.querySelector(`[data-price-id="${CSS.escape(flash)}"]`);
    if (!el) return;
    flashed.current = flash;
    el.scrollIntoView?.({ block: 'center' });
    window.setTimeout(() => setFlash((f) => (f === flash ? null : f)), 2500);
  });
  const supplier = suppliers.data?.suppliers.find((s) => s.id === id);
  usePlaceTitle(supplier ? `Katalog dostawcy — ${supplier.name}` : 'Katalog dostawcy');
  if (!me) return null;
  const seePrice = fieldVisible(me, PRICES_RESOURCE, 'priceDayGrosze');
  const seeClientPrice = fieldVisible(me, PRICES_RESOURCE, 'clientPriceDayGrosze');
  const seeQty = fieldVisible(me, PRICES_RESOURCE, 'quantity');
  const seeDescription = fieldVisible(me, PRICES_RESOURCE, 'description');
  const seeNotes = fieldVisible(me, PRICES_RESOURCE, 'notes');
  const all = prices.data?.prices ?? [];
  // the category filter takes the category with its subcategories, as in our catalog
  const inCategory = (p: PriceView) => {
    if (!categoryId) return true;
    for (let c: string | null | undefined = p.categoryId; c; c = categories.find((x) => x.id === c)?.parentId) if (c === categoryId) return true;
    return false;
  };
  const list = filterPositions(all, search).filter(inCategory);
  const groups = supplierGroups(list, categories);
  const columns = 2 + (seePrice ? 1 : 0) + (seeClientPrice ? 1 : 0) + (seeQty ? 1 : 0) + (seeDescription ? 1 : 0) + (seeNotes ? 1 : 0);
  const removeError = remove.error instanceof ApiError ? remove.error : null;
  const mayEdit = !pick && can(me, PRICES_RESOURCE, 'edit');

  return (
    <section className="page wide supplier-scope" data-feature-id="subrentals.supplier_catalog">
      <SupplierBar title={`Katalog dostawcy: ${supplier?.name ?? '…'}`} pick={pick} back />
      {pick && <PickNotice pick={pick} />}
      <div className="filters">
        <label className="field">
          <span>Szukaj</span>
          <input type="search" value={search} onChange={(e) => update({ szukaj: e.target.value })} placeholder="Nazwa, opis, uwagi" autoComplete="off" />
        </label>
        {canViewCategories && (
          <label className="field">
            <span>Kategoria</span>
            <select value={categoryId} onChange={(e) => update({ kategoria: e.target.value })}>
              <option value="">Wszystkie</option>
              {categoryLabels(categories).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="supplier-add supplier-actions">
          {!pick && mayMatchCatalog(me) && !matching && (
            <button className="btn" type="button" onClick={() => setMatching(true)} data-feature-id="subrentals.catalog_match">
              Dopasuj do mojego katalogu
            </button>
          )}
          {!pick && can(me, PRICES_RESOURCE, 'create') && !adding && (
            <button className="btn primary" type="button" onClick={() => setAdding(true)}>
              Dodaj pozycję
            </button>
          )}
        </div>
      </div>
      {matching && <CatalogMatchPanel supplierId={id} onClose={() => setMatching(false)} />}
      {back && (
        <div className="pick-bar" data-feature-id="catalog.open_from_line">
          <Link className="btn small" to={back}>
            ← Wróć
          </Link>
        </div>
      )}
      {missing && (
        <p className="notice" role="status">
          Tej pozycji nie ma już w katalogu{supplier ? ` dostawcy ${supplier.name}` : ''}.
        </p>
      )}
      {added && (
        <Notice onClose={() => setAdded(null)}>
          Dodano „{added.name}” do katalogu dostawcy{supplier ? ` ${supplier.name}` : ''}.{' '}
          {added.back && (
            <Link className="btn small primary" to={added.back}>
              Wróć
            </Link>
          )}
        </Notice>
      )}
      {deepAdd && addItem.error && <p className="alert" role="alert">Nie udało się wczytać sprzętu do dodania — dodaj pozycję ręcznie.</p>}
      {deepAdd && addItem.data && mayAddSupplierPosition(me) && (
        <PriceForm
          key={deepAdd.itemId}
          supplierId={id}
          initialItem={{ id: addItem.data.id, name: addItem.data.name, categoryId: addItem.data.categoryId ?? null }}
          onSaved={() => setAdded({ name: addItem.data!.name, back: deepAdd.back })}
          onClose={() => update({ dodaj: null, powrot: null })}
        />
      )}
      {adding && <PriceForm supplierId={id} onClose={() => setAdding(false)} />}
      {prices.isLoading && (
        <p className="muted" role="status">
          Wczytywanie…
        </p>
      )}
      {prices.error && <p className="alert" role="alert">{prices.error instanceof ApiError ? prices.error.message : 'Nie udało się wczytać katalogu dostawcy'}</p>}
      {removeError && (
        <p className="alert" role="alert">
          {removeError.status === 409 ? 'Ktoś w międzyczasie zmienił tę pozycję — lista jest odświeżona, spróbuj jeszcze raz.' : removeError.message}
        </p>
      )}
      {prices.data && all.length === 0 && !adding && <p className="muted">Katalog tego dostawcy jest pusty.</p>}
      {prices.data && all.length > 0 && list.length === 0 && <p className="muted">Nie znaleziono pozycji.</p>}
      {list.length > 0 && (
        <table className="table cat-table supplier-table" aria-label="Katalog dostawcy">
          <thead>
            <tr>
              <th scope="col">Nazwa</th>
              {seePrice && (
                <th scope="col" className="num supplier-price">
                  Cena wynajmu
                </th>
              )}
              {seeClientPrice && (
                <th scope="col" className="num supplier-price">
                  Cena dla klienta
                </th>
              )}
              {seeQty && (
                <th scope="col" className="num">
                  Ilość posiadanych
                </th>
              )}
              {seeDescription && (
                <th scope="col" className="hide-phone">
                  Opis
                </th>
              )}
              {seeNotes && (
                <th scope="col" className="hide-phone">
                  Uwagi
                </th>
              )}
              <th scope="col" className="cat-actions-col">
                <span className="visually-hidden">Działania</span>
              </th>
            </tr>
          </thead>
          {groups.map((g) => (
            <Fragment key={g.label ?? 'all'}>
              {g.label && (
                <tbody className={`cat-group${g.depth > 0 ? ' sub' : ''}`} data-depth={g.depth}>
                  <tr>
                    <th scope="rowgroup" colSpan={columns} aria-label={g.label}>
                      <span className="cat-group-name">{g.name}</span>
                    </th>
                  </tr>
                </tbody>
              )}
              <tbody>
                {g.items.map((p) =>
                  editing === p.id ? (
                    <tr key={p.id} className={`row-editor${flash === p.id ? ' row-flash' : ''}`} data-price-id={p.id}>
                      <td colSpan={columns}>
                        <PriceForm supplierId={id} price={p} onClose={() => setEditing(null)} onSubmitted={back && fromLine === p.id ? () => navigate(back) : undefined} />
                      </td>
                    </tr>
                  ) : (
                    <Fragment key={p.id}>
                    <tr data-price-id={p.id} className={flash === p.id ? 'row-flash' : undefined}>
                      <td>{positionName(p)}</td>
                      {seePrice && <td className="num supplier-price">{p.priceDayGrosze !== undefined ? `${formatPln(p.priceDayGrosze)} / dobę` : ''}</td>}
                      {seeClientPrice && <td className="num supplier-price">{p.clientPriceDayGrosze != null ? `${formatPln(p.clientPriceDayGrosze)} / dobę` : '—'}</td>}
                      {seeQty && <td className="num">{p.quantity ?? '—'}</td>}
                      {seeDescription && <td className="hide-phone">{p.description}</td>}
                      {seeNotes && <td className="hide-phone">{p.notes}</td>}
                      <td className="cat-actions">
                        {pick ? (
                          <button className="btn small primary" type="button" aria-label={`Wybierz: ${positionName(p)}`} onClick={() => navigate(pick.returnTo(p, id))}>
                            Wybierz
                          </button>
                        ) : (
                          <>
                            {seeStats && (
                              <button className="btn small ghost" type="button" aria-expanded={open === p.id} aria-controls={`details-${p.id}`} aria-label={`Szczegóły: ${positionName(p)}`} onClick={() => setOpen(open === p.id ? null : p.id)}>
                                Szczegóły
                              </button>
                            )}
                            {mayEdit && (
                              <button className="btn small" type="button" aria-label={`Edytuj: ${positionName(p)}`} onClick={() => setEditing(p.id)}>
                                Edytuj
                              </button>
                            )}
                            <RowMenu label={`Więcej działań: ${positionName(p)}`} actions={can(me, PRICES_RESOURCE, 'delete') ? [{ label: 'Usuń z katalogu dostawcy', danger: true, onSelect: () => remove.mutate(p) }] : []} />
                          </>
                        )}
                      </td>
                    </tr>
                    {open === p.id && !pick && (
                      <tr className="supplier-details" id={`details-${p.id}`}>
                        <td colSpan={columns}>
                          {stats.isLoading && <span className="muted">Wczytywanie…</span>}
                          {stats.error && <span className="alert">{stats.error instanceof ApiError ? stats.error.message : 'Nie udało się wczytać statystyk'}</span>}
                          {stats.data && (
                            <ul className="supplier-stats" aria-label={`Statystyki: ${positionName(p)}`}>
                              {positionStatsLines(
                                stats.data.stats.find((s) => s.priceId === p.id),
                                stats.data.stats.some((s) => 'costGrosze' in s),
                              ).map((line) => (
                                <li key={line}>{line}</li>
                              ))}
                              <li className="muted">liczone ze zwróconych dopożyczeń</li>
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ),
                )}
              </tbody>
            </Fragment>
          ))}
        </table>
      )}
    </section>
  );
}
